import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion } from '@theme/utilities';
import { initAllBuyButtonsBlocks } from '@theme/buy-buttons-store-selector';

/**
 * Cached PDP HTML older than this is treated as stale. The flyout's cached
 * variant-picker markup carries variant ids + `available` flags; inventory can
 * deplete while the page sits open, so an unbounded cache risks submitting a
 * sold-out variant and tripping a Shopify stock error even when aggregate stock
 * exists. The cache is also cleared outright on every successful cart update
 * (see `#handleCartUpdate`) — the TTL just covers server-side drift with no
 * local cart activity.
 */
const PDP_CACHE_TTL_MS = 60000;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #cartUpdateAbort = null;
  /** @type {Map<string, { grid: Element, ts: number }>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productLink = this.#getProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Product surface wrapping quick-add: `product-card` (Bakers/default) or `product-tile` (COBS).
   * @returns {HTMLElement | null}
   */
  #getProductSurface() {
    return this.closest('product-card') ?? this.closest('product-tile');
  }

  /**
   * PDP link from the surrounding product card or COBS product tile.
   * @returns {HTMLAnchorElement | null}
   */
  #getProductLink() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    if (productCard) {
      return productCard.getProductCardLink();
    }

    const productTile = this.closest('product-tile');
    if (!productTile) return null;

    const tileLink = productTile.querySelector('a.product-tile__link');
    return tileLink instanceof HTMLAnchorElement ? tileLink : null;
  }

  /**
   * Gets the currently selected variant ID from the product card or tile
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const surface = this.#getProductSurface();
    if (!surface) return null;

    const checkedInput = /** @type {HTMLInputElement | null} */ (
      surface.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Inventory/availability can change after a product's PDP HTML is cached
    // (this shopper or another buying it). Drop the cache on every successful
    // cart update so the next flyout open re-fetches fresh variant state and
    // can't submit a stale/sold-out variant.
    this.#cartUpdateAbort = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbort.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#cartUpdateAbort?.abort();
  }

  /** @param {CartUpdateEvent} event */
  #handleCartUpdate = (event) => {
    // Keep the cache after a failed add — nothing changed server-side.
    if (event?.detail?.data?.didError) return;
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    const isCommerceEnabled = document.documentElement.dataset.commerceEnabled === 'true';
    if (!isCommerceEnabled) return;

    // Engage the loading state SYNCHRONOUSLY — before any awaits — so the
    // spinner replaces the choose-options icon on the same frame as the
    // click. The button uses the same `.add-to-cart-text` / `::after`
    // markup as the ATC button, so the shared `.atc-loading` CSS in
    // `snippets/add-to-cart-button.liquid` handles the visual swap
    // without new styles. On the first click for a product the
    // `await this.fetchProductPage(...)` below downloads the full PDP
    // HTML which can take 200–800ms on a cold cache — previously the
    // user got zero feedback during that window. Subsequent clicks hit
    // the in-memory cache so the spinner flashes off within a frame,
    // which is the expected "instant" feel.
    const choosingButton = /** @type {HTMLButtonElement|null} */ (
      event.target instanceof Element
        ? event.target.closest('.quick-add__button--choose')
        : this.querySelector('.quick-add__button--choose')
    );
    choosingButton?.classList.add('atc-loading');
    choosingButton?.setAttribute('aria-busy', 'true');

    try {
      const currentUrl = this.productPageUrl;

      // Use cached content only when it's still fresh (see PDP_CACHE_TTL_MS).
      const cached = this.#cachedContent.get(currentUrl);
      let productGrid =
        cached && Date.now() - cached.ts < PDP_CACHE_TTL_MS ? cached.grid : undefined;

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, { grid: productGrid, ts: Date.now() });
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
      }

      this.#openQuickAddModal();
    } finally {
      // Always unwind the loading state — covers cache miss, fetch
      // failure (fetchProductPage returns null), modal-update throw,
      // and the happy path. Modal-open is dialog-managed from here so
      // the user's next interaction surface is the modal itself, not
      // this button.
      choosingButton?.classList.remove('atc-loading');
      choosingButton?.removeAttribute('aria-busy');
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncBackgroundImageToModal(dialogComponent);

    dialogComponent.showDialog();
  };

  /**
   * Copies the product-card scoped background image var onto the shared quick-add modal.
   * @param {QuickAddDialog} dialogComponent
   */
  #syncBackgroundImageToModal(dialogComponent) {
    const backgroundImage = getComputedStyle(this).getPropertyValue('--quick-add-background-image').trim();
    /** @type {HTMLDialogElement | null} */
    const dialog = dialogComponent.querySelector('dialog');
    if (!dialog) return;

    dialog.style.setProperty('--quick-add-background-image', backgroundImage || 'none');
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#insertViewFullProductLink(modalContent);
    this.#cloneTitlePriceAndCaloriesIntoMedia(modalContent);

    // PDP buy-buttons rely on buy-buttons-store-selector.js (DOMContentLoaded + variant:update).
    // Morphed flyout content is inserted later, so run the same init here.
    initAllBuyButtonsBlocks();
  }

  /**
   * Calories block with rendered content (not the empty style-only wrapper).
   * @param {ParentNode | null | undefined} root
   * @returns {HTMLElement | null}
   */
  #findCaloriesBlock(root) {
    if (!root) return null;

    for (const block of root.querySelectorAll('.amb-calories')) {
      if (block instanceof HTMLElement && block.querySelector('.text-block')) {
        return block;
      }
    }

    return null;
  }

  /**
   * Clones product title, optional COBS calories, and price into `.product-information__media`.
   * @param {Element} modalContent
   */
  #cloneTitlePriceAndCaloriesIntoMedia(modalContent) {
    const media = modalContent.querySelector('.product-information__media');
    if (!media) return;

    media.querySelector('[data-quick-add-media-clone]')?.remove();

    const details = modalContent.querySelector('.product-details');
    /** @type {HTMLElement | null} */
    let titleBlock = null;
    /** @type {Element | null} */
    let priceEl = null;
    /** @type {HTMLElement | null} */
    let caloriesEl = null;

    if (details) {
      titleBlock = details.querySelector('.view-product-title');
      priceEl = details.querySelector('product-price');
      caloriesEl = this.#findCaloriesBlock(details);
    } else {
      const header = modalContent.querySelector('.product-header');
      priceEl = modalContent.querySelector('product-price');
      const link = header?.querySelector('a');
      if (link && priceEl) {
        const wrap = document.createElement('div');
        wrap.className = 'view-product-title';
        wrap.appendChild(link.cloneNode(true));
        titleBlock = wrap;
      }
    }

    if (!caloriesEl) {
      caloriesEl = this.#findCaloriesBlock(this.#getProductSurface());
    }

    if (!priceEl || !titleBlock) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'quick-add-modal__media-meta';
    wrapper.setAttribute('data-quick-add-media-clone', '');

    if (details) {
      wrapper.appendChild(titleBlock.cloneNode(true));
    } else {
      wrapper.appendChild(titleBlock);
    }

    if (caloriesEl) {
      const priceRow = document.createElement('div');
      priceRow.className = 'quick-add-modal__price-row';
      priceRow.appendChild(caloriesEl.cloneNode(true));
      priceRow.appendChild(priceEl.cloneNode(true));
      wrapper.appendChild(priceRow);
    } else {
      wrapper.appendChild(priceEl.cloneNode(true));
    }

    const gallery = media.querySelector('media-gallery');
    if (gallery) {
      gallery.insertAdjacentElement('afterend', wrapper);
    } else {
      media.appendChild(wrapper);
    }
  }

  /**
   * Inserts “View full product information” below buy-buttons (or after the form on mobile).
   * @param {Element} modalContent
   */
  #insertViewFullProductLink(modalContent) {
    const templateEl = document.getElementById('quick-add-view-full-product-template');
    if (!(templateEl instanceof HTMLTemplateElement) || !templateEl.content.firstElementChild) return;

    const buyButtons = modalContent.querySelector('.buy-buttons-block');
    const productForm = modalContent.querySelector('product-form-component');
    const insertAfter = buyButtons ?? productForm;
    if (!insertAfter) return;

    if (insertAfter.nextElementSibling?.classList?.contains('quick-add-modal__view-full-product')) {
      return;
    }

    const fragment = templateEl.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment) || !fragment.firstElementChild) return;
    const root = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const link = root.querySelector('[data-quick-add-view-full-link]');
    if (link instanceof HTMLAnchorElement) {
      link.href = this.productPageUrl;
    }
    insertAfter.insertAdjacentElement('afterend', root);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    if (!anchorElement) return;

    for (const link of this.querySelectorAll('.view-product-title a, .product-header a')) {
      if (link instanceof HTMLAnchorElement) link.href = anchorElement.href;
    }

    const viewFullProductLink = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('[data-quick-add-view-full-link]')
    );
    if (viewFullProductLink) viewFullProductLink.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
